// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useUserPermissions from '@Hooks/useUserPermissions';
import { AdminPermissionsEnum, WebhookOutput } from 'thehive-sdk';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import { useGenericQuery } from '@Queries/genericQueries';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { EndpointManage } from '@Organisms/Notification/NotificationManage/EndpointManage/EndpointManage';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { endpointLogos } from '@Pages/Admin/Organisations/Details/Endpoints/List';
import { Space } from 'antd';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';

import './GlobalEndpoints.scss';

const GlobalEndpoints = (): ReactElement => {
    const i = useLocalMessage();

    const {
        data: webhooks,
        refetch: listRefetch,
        status,
    } = useGenericQuery({
        fetchCallback: () => client.NotificationController.listEndpoints(),
        key: ['list-all-global-endpoints'],
    });
    const { licenseCapabilities } = useContext(StatusContext);

    const notificationCapability = useMemo<string[]>(
        () => Object.keys(licenseCapabilities.notification || {}),
        [licenseCapabilities],
    );

    const webhookList = useMemo(() => webhooks?.global || [], [webhooks?.global]);

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_CONFIG] });

    const columns = columnsRenderer<WebhookOutput>([
        {
            title: <ColumnTitle>{i('common.name')}</ColumnTitle>,
            className: 'global-endpoints-list__column__name custom-table__cell',
            render: ({ name }): ReactElement => <>{name}</>,
        },
        {
            title: <ColumnTitle>{i('common.type')}</ColumnTitle>,
            className: 'global-endpoints-list__column__type custom-table__cell',
            render: ({ type = 'webhook' }) => (
                <Space>
                    {endpointLogos[type]}
                    {type
                        .split('')
                        .map((char, index) => (index === 0 ? char.toUpperCase() : char))
                        .join('')}
                </Space>
            ),
        },
        {
            title: <ColumnTitle>{i('endpoint.list.url-token')}</ColumnTitle>,
            className: 'global-endpoint-list__column__url custom-table__cell',
            render: ({ url, token, type }) => {
                const maskedToken = ''.padStart(16, '*');
                return <>{type === 'teams' ? maskedToken : url || `${token?.substring(0, 16)}...`}</>;
            },
        },
        {
            className: 'global-endpoints-list__column__actions custom-table__cell',
            render: (webhook): ReactElement => (
                <ActionsList
                    actionList={{
                        edit: {
                            label: i('general.edit'),
                            icon: <ICONS.EditIcon />,
                            CustomComponent: (triggerElem) => (
                                <EndpointManage
                                    triggerElem={triggerElem}
                                    onFinish={({ type, ...endpoint }) =>
                                        client.NotificationController.addGlobalEndpoint({
                                            ...endpoint,
                                            type: type?.name,
                                        })
                                            .then(() => listRefetch())
                                            .catch(handleNotificationError)
                                    }
                                    defaultValues={webhook}
                                    notificationCapability={notificationCapability}
                                />
                            ),
                        },
                        delete: {
                            label: i('general.delete'),
                            icon: <ICONS.DeleteIcon />,
                            query: () => {
                                client.NotificationController.deleteGlobalEndpoint(webhook.name)
                                    .then(() => {
                                        listRefetch();
                                        openMessage(
                                            'success',
                                            i('notification.notifiers.endpoint.alert.success.deleted'),
                                        );
                                    })
                                    .catch(handleNotificationError);
                            },
                        },
                    }}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    return (
        <THLayout>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <EndpointManage
                                canEditOrganisation
                                notificationCapability={notificationCapability}
                                onFinish={({ type, ...webhook }) =>
                                    client.NotificationController.addGlobalEndpoint({ ...webhook, type: type?.name })
                                        .then(() => {
                                            listRefetch();
                                            openMessage(
                                                'success',
                                                i('notification.notifiers.endpoint.alert.success.created'),
                                            );
                                        })
                                        .catch(handleNotificationError)
                                }
                                triggerElem={<AddButton label={i('endpoint.actions.create.new')} />}
                            />
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={webhookList.sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB))}
                    columns={columns}
                    rowKey="name"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('endpoint.empty')}>
                            {hasPermissions && (
                                <EndpointManage
                                    canEditOrganisation
                                    notificationCapability={notificationCapability}
                                    onFinish={({ type, ...webhook }) =>
                                        client.NotificationController.addGlobalEndpoint({
                                            ...webhook,
                                            type: type?.name,
                                        })
                                            .then(() => {
                                                listRefetch();
                                                openMessage(
                                                    'success',
                                                    i('notification.notifiers.endpoint.alert.success.created'),
                                                );
                                            })
                                            .catch(handleNotificationError)
                                    }
                                    triggerElem={<THButton type="link">{i('endpoint.actions.create.new')}</THButton>}
                                />
                            )}
                        </DisplayEmptyData>
                    }
                />
            </THList>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'mainLayout.adminMenu.global-endpoints',
        icon: <ICONS.NetworkWiredOutlined />,
    },

    path: RouteBuilder.compute('administration.global-endpoints'),
    content: <GlobalEndpoints />,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_CONFIG],
    shared: true,
    space: ['admin'],

    breadcrumb: { label: 'breadcrumb.global.global-endpoints' },
};
